import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { hasAnyGradingSchemeContent } from '../../utils/solution-completeness.js';
import { ambiguousTaskRefMessage, resolveRequestedTaskRefs } from './resolve-requested-task-refs.js';

export const refineMasterSolutionToolDef = [
  {
    type: 'function',
    function: {
      name: 'refine_master_solution',
      description:
        'Überarbeitet eine spezifische Musterlösung basierend auf dem Feedback des Nutzers (z.B. "bei Aufgabe 3 ist 255 richtig"). Darf aufgerufen werden, wenn bereits eine Musterlösung existiert und der Nutzer eine inhaltliche Änderung wünscht.',
      parameters: {
        type: 'object',
        properties: {
          taskNumbers: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Liste von Aufgabennummern, deren Musterlösungen angepasst werden sollen, als VOLLSTÄNDIGE, punktgetrennte Aufgabennummern von der obersten Ebene an (z.B. ["3.a", "3.b"]). Nennt der Nutzer nur die Bezeichnung einer Teilaufgabe ("a") und kommt diese unter mehreren Aufgaben vor (z.B. 1.a UND 2.a), dann NIEMALS vorsorglich beide eintragen und niemals raten: frage den Nutzer zurück, welche Aufgabe gemeint ist, und rufe dieses Tool erst nach seiner Antwort auf.',
          },
          feedback: {
            type: 'string',
            description: 'Das konkrete Feedback oder die Anweisung des Nutzers zur Anpassung.',
          },
        },
        required: ['taskNumbers', 'feedback'],
      },
    },
  },
];

export async function handleRefineMasterSolution(exam: Exam, toolCallArgs: any): Promise<object> {
  const examId = exam._id?.toString();
  if (!examId) {
    return { content: 'Fehler: Keine gültige Klausur-ID für die Anpassung der Musterlösung gefunden.' };
  }

  // WHICH TASK A TYPED REF MEANS, resolved before any job runs. `ms-generation-agent`'s own `findWithRoot`
  // matches a bare label at ANY depth and keeps the FIRST hit, so "a" on an exam with 1.a and 2.a would
  // silently refine whichever the walk reached first — see `resolveRequestedTaskRefs`.
  const requestedTaskNumbers: string[] = Array.isArray(toolCallArgs?.taskNumbers) ? toolCallArgs.taskNumbers : [];
  const examCollectionForRefs = await getCollection(Exam);
  const examForRefs = await examCollectionForRefs.findOne({ _id: examId });
  const resolvedRefs = resolveRequestedTaskRefs((examForRefs as any)?.tasks, requestedTaskNumbers);
  if (resolvedRefs.status === 'ambiguous') {
    logger.warn(
      { examId, ref: resolvedRefs.ref, candidates: resolvedRefs.candidates, context: 'refineMasterSolution' },
      'Ambiguous task ref — refusing to refine master solution',
    );
    return { content: ambiguousTaskRefMessage(resolvedRefs.ref, resolvedRefs.candidates, 'die Musterlösung angepasst') };
  }
  const taskNumbers = resolvedRefs.refs;

  // 1) Synchronous: refine the master solution itself — that's the change the user explicitly asked
  //    for, so it must be done before we report back.
  // Through the `msRefine` JOB, waited on: the teacher is told about this in the same turn, so it still has
  // to finish first — but a restart mid-refine now resumes it instead of losing it.
  const { dispatchJobsAndWait } = await import('../jobs/dispatch.js');
  const { REFINE_JOB_TYPE } = await import('../jobs/job.js');
  const { outcome } = await dispatchJobsAndWait(REFINE_JOB_TYPE.masterSolution, examId, {
    taskRefs: taskNumbers,
    feedback: toolCallArgs.feedback,
  });
  // A failed refine reported as a finished one is worse than either: the teacher is told their correction
  // landed, and the steps below would then align a scheme to a solution that never changed.
  if (outcome === 'failed') {
    return { content: 'Fehler: Die Musterlösung konnte nicht überarbeitet werden.' };
  }
  if (outcome === 'pending') {
    return { content: 'SYSTEM INFO: Die Anpassung der Musterlösung läuft noch im Hintergrund weiter.' };
  }
  const refinedTaskRefs: string[] = taskNumbers;

  // 2) Synchronous gate check. The downstream steps (refine the grading scheme to match, then
  //    regrade) are expensive, so we only decide here whether they apply — the work itself runs
  //    async below. Regrading only follows a grading-scheme refinement, and a grading scheme only
  //    exists once it has been generated or extracted; grading can only have started if it does.
  //
  //    Read off the CONTENT, not the progress status: extraction and generation share one state now, so a
  //    generation that is currently filling gaps puts it back to IN_PROGRESS even though a grading scheme
  //    is right there in the tasks — the old two-state check happened to still see the finished extraction.
  const examCollection = await getCollection(Exam);
  const currentExam = await examCollection.findOne({ _id: examId });
  const state = currentExam?.examState;
  const gradingSchemeExists = hasAnyGradingSchemeContent((currentExam as any)?.tasks);
  const willRefineGradingScheme = gradingSchemeExists && refinedTaskRefs.length > 0;
  const willRegrade = willRefineGradingScheme && !!state?.hasGradingStarted;

  // 3) Async branch: refine the grading scheme to match the new master solution, then (only if
  //    grading already happened) regrade the affected submissions and announce the finished regrade.
  //    The grading-scheme refinement itself is intentionally NOT announced; the regrade announcement
  //    refers back to the original trigger via cause 'master_solution_update'.
  // NOTHING IS DISPATCHED FROM HERE. The scheme refine and its re-grade are both started by the
  // master-solution step's closure (`steps/master-solution.step.ts`), which the `msRefine` row above
  // triggers on its terminal write.

  // 4) Tool result: describe exactly what is finished and what is now running in the background, so
  //    the agent can inform the user accurately.
  let content: string;
  if (willRegrade) {
    content =
      'SYSTEM INFO: Die Musterlösung wurde angepasst. Basierend darauf werden jetzt im Hintergrund das ' +
      'Bewertungsschema angepasst und anschließend die Schülerabgaben neu bewertet. Informiere den ' +
      'Nutzer, dass diese Schritte laufen – das Ergebnis der Neubewertung folgt automatisch, sobald es vorliegt.';
  } else if (willRefineGradingScheme) {
    content =
      'SYSTEM INFO: Die Musterlösung wurde angepasst. Basierend darauf wird jetzt im Hintergrund auch das ' +
      'Bewertungsschema an die neue Musterlösung angepasst. Informiere den Nutzer entsprechend.';
  } else {
    content = 'SYSTEM INFO: Musterlösung angepasst.';
  }
  return { content };
}

export function refineMasterSolutionAvailability(_examState: ExamState): boolean {
  return true;
}
